import type { Table } from '@tanstack/react-table';
import React from 'react';
import type { DataTableLeafColumn, SearchValues, SortConfig, SortDirection } from './types';
import { getSearchDisplayValue, getStickyLeftOffset, type GroupHeaderCell } from './utils';
import { TABLE_THEME } from '@/components/custom/erp/table-theme';
import { LeafHeaderCell } from './leaf-header-cell';

import { cn } from '@/lib/utils';

interface ErpDataTableHeadProps {
  table: Table<any>;
  leafColumns: DataTableLeafColumn[];
  stickyLeafColumns: DataTableLeafColumn[];
  dataLeafColumns: DataTableLeafColumn[];
  renderedColumnWidths: Record<string, number>;
  groupedHeaderRows: {
    maxDepth: number;
    rows: GroupHeaderCell[][];
  };
  searchValues: SearchValues;
  sortConfig: SortConfig | null;
  resolvedHeaderHeight: number;
  resolvedColumnHeaderHeight: number;
  groupRowHeight: number;
  checkboxSelection: boolean;
  showIndexColumn: boolean;
  disableColumnFilters?: boolean;
  allRowsSelected: boolean;
  someRowsSelected: boolean;
  onSearch: (field: string, value: string | string[]) => void;
  onSort: (field: string, direction: SortDirection) => void;
  onHideColumn: (field: string) => void;
  onToggleAllRows: (checked: boolean) => void;
}

export function ErpDataTableHead({
  table,
  leafColumns,
  stickyLeafColumns,
  dataLeafColumns,
  renderedColumnWidths,
  groupedHeaderRows,
  searchValues,
  sortConfig,
  resolvedHeaderHeight,
  resolvedColumnHeaderHeight,
  groupRowHeight,
  checkboxSelection,
  showIndexColumn,
  disableColumnFilters = false,
  allRowsSelected,
  someRowsSelected,
  onSearch,
  onSort,
  onHideColumn,
  onToggleAllRows
}: ErpDataTableHeadProps) {
  const flatHeaders = table.getFlatHeaders();
  const flatHeadersById = new Map(flatHeaders.map(header => [header.column.id, header] as const));
  const selectionState = {
    checked: allRowsSelected,
    indeterminate: !allRowsSelected && someRowsSelected
  };
  const commonLeafHeaderCellProps = {
    onSearch,
    onSort,
    onHideColumn,
    disableColumnFilter: disableColumnFilters,
    selectionState,
    onToggleAllRows
  };
  const hasGroupedHeaders = groupedHeaderRows.maxDepth > 0;

  if (hasGroupedHeaders) {
    return (
      <thead className={cn('sticky top-0 z-20', TABLE_THEME.header)}>
        {groupedHeaderRows.rows.map((groupRow, groupRowIndex) => (
          <tr key={`group-row-${groupRowIndex}`}>
            {groupRowIndex === 0 &&
              stickyLeafColumns.map(column => {
                const tableColumn = table.getColumn(column.id);

                return (
                  <LeafHeaderCell
                    key={column.id}
                    meta={column}
                    width={renderedColumnWidths[column.id] ?? tableColumn?.getSize() ?? column.width}
                    rowSpan={groupedHeaderRows.maxDepth + 1}
                    searchValue=''
                    sortDirection={null}
                    headerHeight={resolvedHeaderHeight}
                    stickyLeft={getStickyLeftOffset(column.sticky, checkboxSelection, showIndexColumn)}
                    {...commonLeafHeaderCellProps}
                  />
                );
              })}

            {groupRow.map((groupCell, groupCellIndex) => (
              <th
                key={groupCell.key}
                colSpan={groupCell.colSpan}
                rowSpan={groupCell.rowSpan}
                className={cn(
                  'border-b border-r border-t p-0 text-center text-[length:var(--erp-data-table-header-text-size)] font-semibold',
                  TABLE_THEME.headerBorder,
                  TABLE_THEME.header,
                  TABLE_THEME.headerText
                )}
                style={{ height: `${groupRowHeight * groupCell.rowSpan}px` }}
              >
                <div className='flex h-full items-center justify-center px-2'>{groupCell.label}</div>
              </th>
            ))}
          </tr>
        ))}

        <tr>
          {dataLeafColumns.map((column, columnIndex) => {
            const tableColumn = table.getColumn(column.id);
            const header = flatHeadersById.get(column.id);
            const field = column.field;

            return (
              <LeafHeaderCell
                key={column.id}
                meta={column}
                width={renderedColumnWidths[column.id] ?? tableColumn?.getSize() ?? column.width}
                searchValue={getSearchDisplayValue(searchValues[field])}
                sortDirection={sortConfig?.field === field ? sortConfig.direction : null}
                headerHeight={resolvedColumnHeaderHeight}
                isLastColumn={columnIndex === dataLeafColumns.length - 1}
                withTopBorder={false}
                canResize={tableColumn?.getCanResize()}
                isResizing={tableColumn?.getIsResizing()}
                onResizeMouseDown={header?.getResizeHandler()}
                onResizeTouchStart={header?.getResizeHandler()}
                onResetSize={() => tableColumn?.resetSize()}
                {...commonLeafHeaderCellProps}
              />
            );
          })}
        </tr>
      </thead>
    );
  }

  return (
    <thead className={cn('sticky top-0 z-20', TABLE_THEME.header)}>
      <tr>
        {leafColumns.map((column, columnIndex) => {
          const tableColumn = table.getColumn(column.id);
          const header = flatHeadersById.get(column.id);
          const field = column.field;

          return (
            <LeafHeaderCell
              key={column.id}
              meta={column}
              width={renderedColumnWidths[column.id] ?? tableColumn?.getSize() ?? column.width}
              searchValue={getSearchDisplayValue(searchValues[field])}
              sortDirection={sortConfig?.field === field ? sortConfig.direction : null}
              headerHeight={resolvedHeaderHeight}
              stickyLeft={getStickyLeftOffset(column.sticky, checkboxSelection, showIndexColumn)}
              isLastColumn={columnIndex === leafColumns.length - 1}
              canResize={tableColumn?.getCanResize()}
              isResizing={tableColumn?.getIsResizing()}
              onResizeMouseDown={header?.getResizeHandler()}
              onResizeTouchStart={header?.getResizeHandler()}
              onResetSize={() => tableColumn?.resetSize()}
              {...commonLeafHeaderCellProps}
            />
          );
        })}
      </tr>
    </thead>
  );
}
